// Inventory state machine (docs/inventory.md).
//
// `durable` transitions are enforced in D1 by the inventory_units trigger.
// Non-durable transitions touch the CART_HOLD live overlay that only the
// PerformanceInventoryDO holds. Identical rows are seeded in
// migrations/0016_reference_data.sql; parity.test.ts keeps them in lockstep.
import {
  HOLD_STATES,
  RESTRICTED_POOL_STATES,
  type HoldState,
  type InventoryState,
  type Permission,
  type RestrictedPoolState,
} from '@bre-ticketing/contracts';

export interface InventoryTransition {
  readonly from: InventoryState;
  readonly to: InventoryState;
  readonly durable: boolean;
  readonly requiredPermission: Permission | null;
}

const transition = (
  from: InventoryState,
  to: InventoryState,
  durable: boolean,
  requiredPermission: Permission | null = null,
): InventoryTransition => ({ from, to, durable, requiredPermission });

const holds: readonly HoldState[] = HOLD_STATES;
const pools: readonly RestrictedPoolState[] = RESTRICTED_POOL_STATES;

export const INVENTORY_TRANSITIONS: readonly InventoryTransition[] = Object.freeze([
  // AVAILABLE
  transition('AVAILABLE', 'CART_HOLD', false),
  transition('AVAILABLE', 'PAYMENT_PENDING', true),
  transition('AVAILABLE', 'EXCHANGE_HOLD', true, 'exchanges.process'),
  ...holds.map((h) => transition('AVAILABLE', h, true, 'inventory.hold.place')),
  ...pools.map((p) => transition('AVAILABLE', p, true, 'inventory.pool.manage')),
  transition('AVAILABLE', 'PRODUCTION_KILL', true, 'inventory.kill'),
  transition('AVAILABLE', 'DISABLED', true, 'inventory.kill'),

  // CART_HOLD (live overlay; releasing it reveals the underlying durable state)
  transition('CART_HOLD', 'AVAILABLE', false),
  ...holds.map((h) => transition('CART_HOLD', h, false)),
  ...pools.map((p) => transition('CART_HOLD', p, false)),
  transition('CART_HOLD', 'PAYMENT_PENDING', false),

  // PAYMENT_PENDING: commit, or return to where the unit came from
  transition('PAYMENT_PENDING', 'SOLD', true),
  transition('PAYMENT_PENDING', 'COMP', true),
  transition('PAYMENT_PENDING', 'AVAILABLE', true),
  ...holds.map((h) => transition('PAYMENT_PENDING', h, true)),
  ...pools.map((p) => transition('PAYMENT_PENDING', p, true)),
  transition('PAYMENT_PENDING', 'EXCHANGE_HOLD', true),
  transition('PAYMENT_PENDING', 'CART_HOLD', false),

  // Holds
  ...holds.map((h) => transition(h, 'AVAILABLE', true, 'inventory.hold.release')),
  ...holds.flatMap((a) => holds.filter((b) => b !== a).map((b) => transition(a, b, true, 'inventory.hold.convert'))),
  ...holds.map((h) => transition(h, 'CART_HOLD', false)),
  ...holds.map((h) => transition(h, 'PAYMENT_PENDING', true)),
  ...holds.map((h) => transition(h, 'EXCHANGE_HOLD', true, 'exchanges.process')),
  ...holds.flatMap((h) => pools.map((p) => transition(h, p, true, 'inventory.pool.manage'))),
  ...holds.map((h) => transition(h, 'PRODUCTION_KILL', true, 'inventory.kill')),
  ...holds.map((h) => transition(h, 'DISABLED', true, 'inventory.kill')),

  // Restricted pools (ADA, companion, restricted view)
  ...pools.map((p) => transition(p, 'AVAILABLE', true, 'inventory.pool.manage')),
  ...pools.map((p) => transition(p, 'CART_HOLD', false)),
  ...pools.map((p) => transition(p, 'PAYMENT_PENDING', true)),
  ...pools.map((p) => transition(p, 'EXCHANGE_HOLD', true, 'exchanges.process')),
  ...pools.flatMap((p) => holds.map((h) => transition(p, h, true, 'inventory.hold.place'))),
  ...pools.map((p) => transition(p, 'DISABLED', true, 'inventory.kill')),

  // SOLD: refund, or release after an exchange-out / void
  transition('SOLD', 'REFUNDED', true),
  transition('SOLD', 'AVAILABLE', true),
  ...holds.map((h) => transition('SOLD', h, true)),
  ...pools.map((p) => transition('SOLD', p, true)),

  // COMP: comp voided
  transition('COMP', 'AVAILABLE', true),
  ...holds.map((h) => transition('COMP', h, true)),
  ...pools.map((p) => transition('COMP', p, true)),

  // REFUNDED: disposition decided after the refund
  transition('REFUNDED', 'AVAILABLE', true),
  ...holds.map((h) => transition('REFUNDED', h, true)),
  ...pools.map((p) => transition('REFUNDED', p, true)),
  transition('REFUNDED', 'PRODUCTION_KILL', true, 'inventory.kill'),
  transition('REFUNDED', 'DISABLED', true, 'inventory.kill'),

  // EXCHANGE_HOLD
  transition('EXCHANGE_HOLD', 'PAYMENT_PENDING', true),
  transition('EXCHANGE_HOLD', 'AVAILABLE', true),
  ...holds.map((h) => transition('EXCHANGE_HOLD', h, true)),
  ...pools.map((p) => transition('EXCHANGE_HOLD', p, true)),

  // PRODUCTION_KILL
  transition('PRODUCTION_KILL', 'AVAILABLE', true, 'inventory.kill'),
  ...holds.map((h) => transition('PRODUCTION_KILL', h, true, 'inventory.hold.place')),
  transition('PRODUCTION_KILL', 'DISABLED', true, 'inventory.kill'),

  // DISABLED
  transition('DISABLED', 'AVAILABLE', true, 'inventory.kill'),
  ...holds.map((h) => transition('DISABLED', h, true, 'inventory.hold.place')),
  ...pools.map((p) => transition('DISABLED', p, true, 'inventory.pool.manage')),
]);

const transitionsByKey = new Map(INVENTORY_TRANSITIONS.map((t) => [`${t.from}->${t.to}`, t]));

export function findInventoryTransition(from: InventoryState, to: InventoryState): InventoryTransition | undefined {
  return transitionsByKey.get(`${from}->${to}`);
}

/** `durableOnly` mirrors what the D1 trigger accepts on inventory_units. */
export function canTransitionInventory(
  from: InventoryState,
  to: InventoryState,
  options: { durableOnly?: boolean } = {},
): boolean {
  const found = findInventoryTransition(from, to);
  return found !== undefined && (!options.durableOnly || found.durable);
}

export function isHoldState(state: InventoryState): state is HoldState {
  return (HOLD_STATES as readonly string[]).includes(state);
}

export function isRestrictedPoolState(state: InventoryState): state is RestrictedPoolState {
  return (RESTRICTED_POOL_STATES as readonly string[]).includes(state);
}

/** A live ticket exists for units in these states. */
export function isOccupiedState(state: InventoryState): boolean {
  return state === 'SOLD' || state === 'COMP';
}

/** States a unit may be carted from, and returned to if the cart or payment fails. */
export function isCartableState(state: InventoryState): boolean {
  return state === 'AVAILABLE' || isHoldState(state) || isRestrictedPoolState(state);
}
